import type { KeyboardEvent, MouseEvent, ReactElement } from 'react';

import {
  DiagnosticsKind,
  type DiagnosticsEntry,
} from '@/utils/diagnostics/diagnosticsLog';

import styles from './EntryTile.module.css';

/** Перенос `_EntryTile` из `diagnostics_page.dart`. */

/** `_kind`: как вид записи называется в списке. */
const KIND_LABEL: Record<DiagnosticsKind, string> = {
  [DiagnosticsKind.failure]: 'сбой',
  [DiagnosticsKind.toast]: 'тост',
  [DiagnosticsKind.request]: 'запрос',
  [DiagnosticsKind.stream]: 'поток',
  [DiagnosticsKind.session]: 'сессия',
  [DiagnosticsKind.event]: 'событие',
};

/** `[time, kind, code, duration].join(' · ')` */
const META_SEPARATOR = ' · ';

export interface EntryTileProps {
  readonly entry: DiagnosticsEntry;
  /** Время записи, уже отформатированное страницей (`_time`). */
  readonly time: string;
  readonly expanded: boolean;
  readonly onTap: () => void;
}

function metaOf(entry: DiagnosticsEntry, time: string): string {
  return [
    time,
    KIND_LABEL[entry.kind],
    ...(entry.code !== undefined ? [entry.code] : []),
    ...(entry.durationMs !== undefined ? [`${entry.durationMs} мс`] : []),
  ].join(META_SEPARATOR);
}

/** Enter и пробел на `role="button"` — то, что кнопке браузер даёт сам. */
function activateOnKey(
  event: KeyboardEvent<HTMLDivElement>,
  onTap: () => void,
): void {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  onTap();
}

/**
 * Строка журнала.
 *
 * `InkWell` здесь — не кнопка, а именно строка: внутри неё лежит
 * `SelectableText` с подробностями, а текст внутри `<button>` браузер
 * выделять не даёт. Поэтому это `div` с ролью кнопки и обработкой клавиш.
 */
export function EntryTile({
  entry,
  time,
  expanded,
  onTap,
}: EntryTileProps): ReactElement {
  // `onTap: entry.details == null ? null : onTap` — строка без подробностей
  // не нажимается.
  const clickable = entry.details !== undefined;
  const className = [styles.tile, expanded ? styles.expanded : null]
    .filter(Boolean)
    .join(' ');
  // `SelectableText` перехватывал нажатие: по тексту подробностей строка
  // не сворачивалась.
  const keepSelection = (event: MouseEvent<HTMLSpanElement>): void =>
    event.stopPropagation();
  return (
    <div
      className={className}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onTap : undefined}
      onKeyDown={clickable ? (event) => activateOnKey(event, onTap) : undefined}
    >
      <span className={[styles.meta, entry.isError ? styles.error : null].filter(Boolean).join(' ')}>
        {metaOf(entry, time)}
      </span>
      <span className={expanded ? styles.title : styles.titleClamped}>
        {entry.title}
      </span>
      {expanded && entry.details !== undefined && (
        <span className={styles.details} onClick={keepSelection}>
          {entry.details}
        </span>
      )}
    </div>
  );
}
